import { DateRangePicker, Tooltip, getDate, getPaymentProcessorTitle } from '@systemeio/ui'
import ContactInfoTooltip from 'modules/contacts/components/contact-info-tooltip'
import ContactPaymentsAmountTooltip from 'modules/contacts/contact-page/components/contact-payments-amount-tooltip'
import ContactPaymentsPaymentTooltip from 'modules/contacts/contact-page/components/contact-paymnet-tooltip'
import { ContactPaymentsStatusEnum } from 'modules/contacts/contact-page/enums/contact-payments-enum'
import { useContactPayments } from 'modules/contacts/contact-page/hooks/use-contact-payments'
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface'
import { ContactPaymentsInterface } from 'modules/contacts/contact-page/types/contact-payments-interface'
import { getContactPaymentUrl } from 'modules/contacts/contact-page/utils/get-contact-payment-url'
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface'
import RefundConfirmation from 'modules/payment/transactions/components/refund-confirmation-modal'
import React, { useEffect } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'
import { OpenInNewIcon, PercentIcon, RefundIcon } from '@systemeio/ui'

interface ContactPaymentsProps {
  contactId: ContactId
  contact?: ContactInterface
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const ContactPayments = ({ contactId, contact }: ContactPaymentsProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )
  const { contactPayments, mutate } = useContactPayments({
    contactId,
    limit,
    pagination,
    filter,
    shouldFetch: filter.date[0] !== null,
  })

  useEffect(() => {
    if (contact) {
      setFilter({ date: getDateRangeSinceDate(contact.createdAt, user?.timezone) })
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactPaymentsInterface>[] = [
    {
      key: 'paymentProcessorName',
      header: '#',
      widthPercent: 15,
      onRender: payment => (
        <div className={'flex gap-1.5'}>
          <ContactInfoTooltip
            fields={payment.customer.fieldValues}
            referredByEmail={payment.customer.referredBy?.email}
          >
            <span>{getPaymentProcessorTitle(payment.paymentProcessorName)}</span>
          </ContactInfoTooltip>
          {payment.status === ContactPaymentsStatusEnum.refunded && payment.refundedAt && (
            <Tooltip
              mode={'hover'}
              label={`${t('global.refunded')} (${dateTimeFormat(payment.refundedAt, {
                timeFormat: user?.timeFormat,
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })})`}
            >
              <RefundIcon className={'fill-orange h-[20px] w-[20px]'} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'paidAt',
      header: t('global.date'),
      widthPercent: 20,
      onRender: payment => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(payment.paidAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span className={'w-full truncate'}>
            {dateFormat(payment.paidAt, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'payment',
      header: t('dashboard.contact_page.total'),
      widthPercent: 15,
      onRender: payment => <ContactPaymentsAmountTooltip contactPayment={payment} />,
    },
    {
      key: 'payment',
      header: t('dashboard.contact_page.plan_product'),
      onRender: payment => (
        <div className={'flex w-full flex-col'}>
          {payment.paymentItems.map((el, idx) => (
            <div
              key={idx}
              className={`py-3 ${idx !== 0 && 'border-text-secondary/30 border-t'} ${
                idx === 0 && 'pt-0'
              } ${idx + 1 === payment.paymentItems.length && 'pb-0'}`}
            >
              <div className={'flex items-center gap-1.5'}>
                <ContactPaymentsPaymentTooltip orderItem={el.orderItem} />
                {payment.customer.referredBy && (
                  <Tooltip label={payment.customer.referredBy.email} mode={'hover'}>
                    <PercentIcon className={'fill-text-tertiary'} />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      ),
      onRenderSmall: payment => (
        <div className={'flex flex-col truncate'}>
          {payment.paymentItems.map((el, idx) => (
            <div
              key={idx}
              className={` truncate py-3 ${idx !== 0 && 'border-text-secondary/30 border-t'} ${
                idx === 0 && 'pt-0'
              } ${idx + 1 === payment.paymentItems.length && 'pb-0'}`}
            >
              <div className={'flex items-center gap-1.5 truncate'}>
                {el.orderItem.offer &&
                el.orderItem.offer.removedAt === null &&
                el.orderItem.offer.funnelStep ? (
                  <LinkWithoutPrefetch
                    rel="noreferrer"
                    className="primary-link w-full truncate"
                    href={getContactPaymentUrl(
                      el.orderItem.offer.funnelStep.funnel.id,
                      el.orderItem.offer.funnelStep.id,
                    )}
                  >
                    {el.orderItem.pricePlan
                      ? el.orderItem.pricePlan.innerName
                      : el.orderItem.productOrderItem && el.orderItem.productOrderItem.product.name}
                  </LinkWithoutPrefetch>
                ) : el.orderItem.pricePlan ? (
                  el.orderItem.pricePlan.innerName
                ) : (
                  el.orderItem.productOrderItem && el.orderItem.productOrderItem.product.name
                )}
                {payment.customer.referredBy && <PercentIcon className={'fill-text-tertiary'} />}
              </div>
            </div>
          ))}
        </div>
      ),
      widthPercent: 30,
    },
  ]

  const onUpdate = async () => {
    await mutate()
  }
  return (
    <ListingLayout
      counter={false}
      data={contactPayments}
      type={'default'}
      title={
        contact ? (
          <LinkWithoutPrefetch
            className={
              'text-text-primary hover:text-brand-primary focus-visible:text-brand-primary group text-[17px] outline-hidden transition-colors duration-200 ease-linear'
            }
            href={`/payment/transactions?filter=%7B%22email%22%3A%22${contact.email}%22%7D`}
          >
            <div className={'flex items-center gap-1.5'}>
              {t('dashboard.contact_page.payments')}
              <OpenInNewIcon
                className={
                  'fill-text-primary group-hover:fill-brand-primary transition-colors duration-200 ease-linear'
                }
              />
            </div>
          </LinkWithoutPrefetch>
        ) : (
          <span className={'text-[17px]'}>{t('dashboard.contact_page.payments')}</span>
        )
      }
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter({ date: date })
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          openLink: [
            {
              name: 'download-invoice',
              caption: t('dashboard.contact_page.download_invoice'),
              setHref: payment => `/payment/sales-invoice/${payment.salesInvoice?.id}/download`,
              openInNew: false,
              shouldRender: payment => payment.salesInvoice !== null,
            },
            {
              name: 'affiliate',
              caption: t('dashboard.user.affiliate'),
              shouldRender: payment => payment.customer.referredBy !== null,
              setHref: payment => `/affiliate-program/${payment.customer.referredBy?.id}`,
              isNext: true,
              openInNew: false,
            },
          ],
          custom: [
            {
              shouldRender: payment => payment.canBeRefunded,
              name: 'refund',
              caption: t('dashboard.contact_page.refund'),
              onActionRender: (opened, onClose, transaction, key) => (
                <RefundConfirmation
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  paymentProcessorName={transaction.paymentProcessorName}
                  transactionId={transaction.id}
                  onUpdate={onUpdate}
                />
              ),
            },
          ],
        }}
        data={contactPayments}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactPayments
